<template>
  <div
    :class="{
      'user-avatar': true,
      'user-avatar--x-small': size === 'x-small',
      'user-avatar--small': size === 'small',
      'user-avatar--medium': size === 'medium',
      'user-avatar--large': size === 'large',
    }"
  >
    <img v-if="showPhoto" class="user-avatar__photo" :src="userPhoto" :alt="name ?? ''" @error="photoFailed = true" />
    <div
      v-else
      :class="{
        'user-avatar__fallback': true,
        'user-avatar__fallback--x-small': size === 'x-small',
        'user-avatar__fallback--small': size === 'small',
        'user-avatar__fallback--medium': size === 'medium',
        'user-avatar__fallback--large': size === 'large',
        'user-avatar__fallback--white': color === 'white',
      }"
    >
      <span v-if="initial">{{ initial }}</span>
      <span v-else class="user-avatar__fallback-icon" v-html="userCircleIcon" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { imagePath } from '@lib/imgproxy';
import userCircleIcon from '@icons/other/user-circle.svg';

const {
  name,
  url,
  size = 'medium',
  color = 'default',
} = defineProps<{
  /** Users without a name exist (invited but never onboarded), so this may be empty. */
  name?: string | null;
  url?: string | null;
  size?: 'x-small' | 'small' | 'medium' | 'large';
  color?: 'default' | 'white';
}>();

const photoFailed = ref(false);
watch(
  () => url,
  () => (photoFailed.value = false)
);

const showPhoto = computed(() => !!url && !photoFailed.value);
const userPhoto = computed(() => imagePath(`/images/${url}`, 50, 50));

const initial = computed(() => name?.trim().charAt(0) ?? '');
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

// User avatar ----------------------------------------------------------
.user-avatar {
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;

  &--x-small {
    min-width: 2rem;
    width: 2rem;
    height: 2rem;
  }

  &--small {
    min-width: 2.4rem;
    width: 2.4rem;
    height: 2.4rem;
  }

  &--medium {
    min-width: 3.2rem;
    width: 3.2rem;
    height: 3.2rem;
  }

  &--large {
    min-width: 4.4rem;
    width: 4.4rem;
    height: 4.4rem;
  }

  &__photo {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__fallback {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 1px solid #ac00e0;
    color: #ac00e0;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: 'Montserrat', sans-serif;
    font-weight: bold;

    &--white {
      color: #fff;
      border-color: #fff;
    }

    &--x-small {
      font-size: 1.2rem;
      line-height: 1.6rem;
    }

    &--small {
      font-size: 1.4rem;
      line-height: 1.8rem;
    }

    &--medium {
      font-size: 2rem;
      line-height: 2.6rem;
      border-width: 2px;
    }

    &--large {
      font-size: 2.8rem;
      line-height: 3.6rem;
      border-width: 2px;
    }
  }

  &__fallback-icon {
    display: flex;
    width: 60%;
    height: 60%;

    :deep(svg) {
      width: 100%;
      height: 100%;
      fill: currentColor;
    }
  }
}
</style>
